
import { useState, useEffect } from 'react';
import { StructuredIngredient, Recipe } from '@/types/recipe';

interface QuickRecipeFormData {
  title: string;
  servings: string;
  instructions: string;
  imageUrl: string;
  userImageUrl: string;
  userImagePath: string;
}

interface RecipeSuccessData {
  id: string;
  title: string;
  isEditing: boolean;
}

/**
 * Hook for managing quick recipe form state
 */
export const useQuickRecipeFormState = (recipe?: Recipe) => {
  const [formData, setFormData] = useState<QuickRecipeFormData>({
    title: recipe?.title || '',
    servings: recipe?.servings || '4',
    instructions: recipe?.instructions?.[0] || '',
    imageUrl: recipe?.image_url || '',
    userImageUrl: recipe?.user_image_url || '',
    userImagePath: recipe?.user_image_path || ''
  });
  
  const [ingredients, setIngredients] = useState<StructuredIngredient[]>([
    { name: '', quantity: null, unit: '' }
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successData, setSuccessData] = useState<RecipeSuccessData | null>(null);

  // Initialize form data when recipe changes
  useEffect(() => {
    if (recipe) {
      setFormData({
        title: recipe.title,
        servings: recipe.servings || '4',
        instructions: recipe.instructions?.[0] || '',
        imageUrl: recipe.image_url || '',
        userImageUrl: recipe.user_image_url || '',
        userImagePath: recipe.user_image_path || ''
      });
      
      // Load ingredients from quickIngredients or structured_ingredients
      const recipeIngredients = recipe.quickIngredients || 
                               recipe.structured_ingredients_metric || 
                               recipe.structured_ingredients_imperial || 
                               [{ name: '', quantity: null, unit: '' }];
      
      setIngredients(recipeIngredients.length > 0 ? recipeIngredients : [{ name: '', quantity: null, unit: '' }]);
    }
  }, [recipe]);

  const handleDialogClose = () => {
    setSuccessData(null);
  };

  return {
    formData,
    setFormData,
    ingredients,
    setIngredients,
    isSubmitting,
    setIsSubmitting,
    successData,
    setSuccessData,
    handleDialogClose
  };
};
